import React from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"><ArrowLeft size={14} /> Home</Link>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-6 lg:px-10 py-14 prose-content">
        <div className="overline">Legal · Policy version 2026-02-01</div>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl text-primary leading-tight">Privacy Policy</h1>
        <p className="mt-4 text-muted-foreground">
          This policy explains how <strong>Arogya Pramana Trust Infrastructure Pvt. Ltd.</strong> (&ldquo;Arogya Pramana&rdquo;, &ldquo;we&rdquo;)
          collects, uses, discloses, retains, and safeguards your personal data — in compliance with India&apos;s
          <strong> Digital Personal Data Protection Act, 2023 (DPDP Act)</strong>.
        </p>

        <Section title="1. Who we are">
          <p><strong>Data Fiduciary:</strong> Arogya Pramana Trust Infrastructure Pvt. Ltd.</p>
          <p><strong>Registered address:</strong> Bandra Kurla Complex, Mumbai 400051, India.</p>
          <p><strong>Data Protection Officer (Grievance Officer):</strong> Dr. Ananya Rao — <a href="mailto:dpo@arogyapramana.in" className="text-primary underline">dpo@arogyapramana.in</a>.</p>
        </Section>

        <Section title="2. Personal data we collect">
          <ul className="list-disc pl-6 space-y-1 text-sm">
            <li>Identity: name, email, mobile number, professional registration numbers (for doctors).</li>
            <li>Sensitive personal data: symptoms, diagnoses, prescriptions, lab reports, care journey (patient-supplied).</li>
            <li>Provider data: hospital operational KPIs, incident reports, department benchmarks.</li>
            <li>Technical: session cookies (essential only), device metadata for security.</li>
          </ul>
        </Section>

        <Section title="3. Purpose of processing">
          <p>We process your data strictly to deliver the Arogya Pramana service — provider discovery, appointment booking,
          Trust Index publication, HCAHPS-based hospital rating, symptom triage guidance, medical record custody, and
          platform administration. We do <strong>not</strong> sell your data. We do not use your data for advertising.</p>
        </Section>

        <Section title="4. Legal basis: your consent">
          <p>Processing is based on the explicit consent you provide at registration. You may withdraw consent at any time from
          <Link to="/settings/privacy" className="text-primary underline"> Privacy &amp; Data</Link> in your account.
          Withdrawal will pause future processing and initiate deletion within 30 days, subject to statutory retention
          requirements (e.g., medical record retention under the Indian Medical Council).</p>
        </Section>

        <Section title="5. Sharing & disclosure">
          <p>We share only what you explicitly instruct — for example, sending an appointment request to a chosen doctor.
          We do not sell, rent, or share personal data with advertisers, brokers, or third-party analytics.</p>
        </Section>

        <Section title="6. Retention">
          <p>Account &amp; profile data: retained while your account is active, deleted within 30 days of a valid deletion request.
          Medical records: retained for up to 7 years per Indian medical record practice unless earlier erased on request.
          Consent logs: retained for the life of the account plus 3 years, for audit.</p>
        </Section>

        <Section title="7. Your DPDP rights">
          <ul className="list-disc pl-6 space-y-1 text-sm">
            <li>Right to access — download a complete copy of your data (JSON export).</li>
            <li>Right to correction — edit inaccurate data in-product; or request via DPO.</li>
            <li>Right to erasure — delete your account and personal data.</li>
            <li>Right to withdraw consent — for marketing (in-product); for processing (via account deletion).</li>
            <li>Right to nominate — designate a person to exercise these rights in the event of incapacity.</li>
            <li>Right to grievance redressal — reach our DPO or file via <Link to="/grievance" className="text-primary underline">/grievance</Link>. SLA: 30 days.</li>
          </ul>
        </Section>

        <Section title="8. Security safeguards">
          <p>Encrypted transport (TLS), bcrypt-hashed passwords, httpOnly cookies, principle of least privilege, role-based access
          control, audit logs of consent changes, and periodic security review.</p>
        </Section>

        <Section title="9. Children">
          <p>Users under 18 require verifiable parental consent. We do not knowingly process a child&apos;s data without such consent.</p>
        </Section>

        <Section title="10. Cross-border transfer">
          <p>Data is primarily processed on infrastructure within India. Any transfer outside India will comply with DPDP Section 16
          and applicable restricted-country notifications.</p>
        </Section>

        <Section title="11. Changes to this policy">
          <p>Material changes are notified in-product 30 days before taking effect, and require re-consent where required by law.</p>
        </Section>

        <div className="mt-10 flex gap-3">
          <Link to="/grievance"><Button variant="outline" className="border-primary text-primary hover:bg-primary/5" data-testid="privacy-grievance-cta">File a grievance</Button></Link>
          <Link to="/dpdp"><Button data-testid="privacy-dpdp-cta">Read DPDP rights</Button></Link>
        </div>
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="font-serif text-2xl text-primary">{title}</h2>
      <div className="mt-2 text-sm text-muted-foreground leading-relaxed space-y-2">{children}</div>
    </section>
  );
}
